/**
 * A question's tariff in the paper's own words — "Q3 · 10 marks — (i) 4 working
 * · (ii) 4 working · option ±2". Pure, so the paper view, the teacher's panel
 * and anything printed say the same thing.
 * See docs/FeatureDesigns/AssessmentAndMarking/paper-stated-marking-target.md §6.2.
 */

import { markingPartRoles, workingAnswerWorthFor, type MarkingScheme } from '@padcanvas/shared';

export interface TariffQuestion {
  questionType?: string | null;
  marks?: number | null;
  parts?: ReadonlyArray<{ partId: string; label?: string; type?: string; marks?: number }> | null;
}

/** "±2" when right and wrong weigh the same, else "+2 / −1"; "+1" when there is no penalty. */
function optionTariff(answer: number, penalty: number): string {
  if (penalty === 0) return `+${answer}`;
  return penalty === answer ? `±${answer}` : `+${answer} / −${penalty}`;
}

/**
 * The tariff line, or undefined when there is nothing to say (no worth, no
 * rule). `worth` is the question's resolved worth on this paper, when known.
 */
export function questionTariff(q: TariffQuestion, scheme: MarkingScheme | undefined, worth?: number): string | undefined {
  if (scheme?.kind === 'working_answer') {
    const total = workingAnswerWorthFor(scheme, { questionType: q.questionType, parts: q.parts ?? undefined });
    const option = `option ${optionTariff(scheme.answerMarks, scheme.wrongPenalty)}`;
    if (q.questionType === 'STRUCTURED' && q.parts?.length) {
      const roles = markingPartRoles(q.parts);
      const working = q.parts
        .filter((_, i) => roles[i] === 'working')
        .map(p => `(${p.label ?? p.partId}) ${scheme.workingMarks} working`);
      return `${total} marks — ${[...working, option].join(' · ')}`;
    }
    return `${total} marks — ${scheme.workingMarks} working · ${option}`;
  }
  const w = worth ?? q.marks ?? undefined;
  return w ? `${w} mark${w === 1 ? '' : 's'}` : undefined;
}

/**
 * Every question's tariff on a paper, keyed by question id: each under its
 * section's rule, else the paper's, at its worth on this paper. One helper so
 * the teacher's and the admin's paper views state the marks identically.
 */
export function paperTariffs(
  paper: {
    marking?: MarkingScheme;
    sections?: ReadonlyArray<{ questionIds: string[]; marking?: MarkingScheme }>;
    questionMarks?: Record<string, number>;
  },
  questions: ReadonlyArray<TariffQuestion & { id: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    const scheme = paper.sections?.find(s => s.questionIds.includes(q.id))?.marking ?? paper.marking;
    const line = questionTariff(q, scheme, paper.questionMarks?.[q.id]);
    if (line) out[q.id] = line;
  }
  return out;
}
